/**
 * DirectorKPIs — Admin Panel headline metrics row (iteration 18 redesign).
 *
 * Reads the current workspace's roster + sent-messages count and shows 4 calm
 * KPI cards (Total Children · Checked In Today · Staff On Duty · Messages New).
 * Mirrors the admin panel reference design but with Alouvya's calm palette.
 */
import { useEffect, useState } from "react";
import { Users, LogIn, BadgeCheck, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

function KpiCard({ label, value, Icon, iconBg, iconFg, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-3xl bg-white border hairline p-5 flex items-center gap-4"
    >
      <span
        className="h-11 w-11 rounded-2xl flex items-center justify-center shrink-0"
        style={{ background: iconBg }}
        aria-hidden
      >
        <Icon size={20} strokeWidth={1.7} style={{ color: iconFg }} />
      </span>
      <div className="min-w-0">
        <div
          data-testid={`${testid}-value`}
          className="font-serif text-3xl text-[#171717] leading-none tabular-nums"
        >
          {value}
        </div>
        <div className="mt-1.5 text-[11px] tracking-[0.18em] uppercase text-[#525252] font-medium">
          {label}
        </div>
      </div>
    </div>
  );
}

export default function DirectorKPIs({ workspaceId, workspaceDetail }) {
  const [roster, setRoster] = useState(null);
  const [newMessages, setNewMessages] = useState(0);

  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    api
      .get(`/workspaces/${workspaceId}/roster`)
      .then((r) => {
        if (alive) setRoster(r.data || { rows: [], counts: {} });
      })
      .catch(() => {});
    api
      .get(`/workspaces/${workspaceId}/announcements/sent?limit=30`)
      .then((r) => {
        if (!alive) return;
        const now = new Date();
        const last24 = (r.data || []).filter((m) => {
          if (m.is_scheduled) return false;
          try {
            return now - new Date(m.created_at) < 24 * 3600 * 1000;
          } catch {
            return false;
          }
        });
        setNewMessages(last24.length);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [workspaceId]);

  const totalChildren = roster?.rows?.length ?? "—";
  const checkedIn = roster?.counts?.present ?? "—";
  const onDuty = (workspaceDetail?.caregivers?.length || 0) +
    (workspaceDetail?.admins?.length || 0);

  return (
    <section
      data-testid="director-kpis"
      className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3"
    >
      <KpiCard
        testid="kpi-total-children"
        label="Total children"
        value={totalChildren}
        Icon={Users}
        iconBg="var(--pastel-yard)"
        iconFg="#1A1B2E"
      />
      <KpiCard
        testid="kpi-checked-in"
        label="Checked in today"
        value={checkedIn}
        Icon={LogIn}
        iconBg="var(--pastel-eat)"
        iconFg="#8A4A3B"
      />
      <KpiCard
        testid="kpi-on-duty"
        label="Staff on duty"
        value={onDuty || "—"}
        Icon={BadgeCheck}
        iconBg="var(--pastel-play)"
        iconFg="#86601A"
      />
      <KpiCard
        testid="kpi-messages-new"
        label="Messages today"
        value={newMessages}
        Icon={MessageCircle}
        iconBg="var(--pastel-care)"
        iconFg="#1F3A2A"
      />
    </section>
  );
}
